import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth, API } from '../App';
import { Building2, ChevronDown, Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

// Visible only to the Platform Owner (super admin). Lets them view/assist
// any subscribing company's workspace, then step back out to their own.
const WorkspaceSwitcher = () => {
  const { user, workspaceId, switchWorkspace } = useAuth();
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    if (user?.is_super_admin) {
      fetchCompanies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchCompanies = async () => {
    try {
      const res = await axios.get(`${API}/companies`);
      setCompanies(res.data);
    } catch (error) {
      console.error('Failed to fetch companies', error);
    }
  };

  if (!user?.is_super_admin) return null;

  const activeCompany = companies.find((c) => c.id === workspaceId);
  const isViewingOther = !!workspaceId && workspaceId !== user.home_company_id;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
            isViewingOther
              ? 'bg-purple-50 border-purple-300 text-purple-700'
              : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100'
          }`}
          data-testid="workspace-switcher-trigger"
        >
          <Building2 size={16} />
          <span>{isViewingOther ? activeCompany?.name || 'Workspace' : 'My Workspace'}</span>
          <ChevronDown size={14} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64" data-testid="workspace-switcher-menu">
        <DropdownMenuLabel>Switch Company Workspace</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {companies.map((c) => (
          <DropdownMenuItem
            key={c.id}
            onClick={() => switchWorkspace(c.id === user.home_company_id ? null : c.id)}
            className="flex items-center justify-between cursor-pointer"
            data-testid={`workspace-option-${c.id}`}
          >
            <div>
              <p className="font-medium">{c.name}{c.is_default ? ' (Default)' : ''}</p>
              <p className="text-xs text-slate-500">{c.user_count} users · {c.call_count} calls</p>
            </div>
            {((workspaceId && workspaceId === c.id) || (!workspaceId && c.id === user.home_company_id)) && (
              <Check size={16} className="text-purple-600" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default WorkspaceSwitcher;
